import { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, Autocomplete, Grid, Typography, Button, TextField, DialogActions, Checkbox } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useSelector } from '../../../../redux/store';
import { getUsers } from '../../../../redux/slices/users';
import { newUser } from '../../../../api/user';
import Iconify from '../../../../reusable-components/iconify';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import { AwaitButton } from '../../../../reusable-components/await-button';
import CloseIconButton from '../../../../reusable-components/basic-buttons/CloseIconButton';
import SimpleAutocompleteProps from '../../../../reusable-components/autocomplete/SimpleAutocompleteProps';
import AddOptionAutocompleteProps from '../../../../reusable-components/autocomplete/AddOptionAutocompleteProps';

export default function NewUserModal() {
  const [open, setOpen] = useState(false);
  const [positionsList, setPositionsList] = useState([]);
  const [userList, setUserList] = useState([]);
  const [invalidPassword, setInvalidPassword] = useState(false);

  const {
    data: { facilities, positions, users },
  } = useSelector((state) => state.users);
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const blankForm = () => ({
    firstName: '',
    lastName: '',
    email: '',
    facilityId: '',
    facility: '',
    position: '',
    positionId: '',
    password: '',
    copyUserId: '',
    dontSendConfirmEmail: false,
  });
  const [value, setValue] = useState(blankForm());
  
  useEffect(() => {
    dispatch(getUsers());
  }, [dispatch]);

  useEffect(() => {
    setUserList(users.map((user) => ({ value: user.id, label: `${user.firstName} ${user.lastName} (${user.email})` })));
  }, [users]);

  const handleSave = async () => {
    const isInvalidPassword = value?.password && !/^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[@$!%*#?&])[\S\s]{8,}$/.test(value.password);
    setInvalidPassword(isInvalidPassword);
    if (isInvalidPassword) return;
    if (users?.some((x) => x.firstName + x.lastName === value.firstName + value.lastName))
      enqueueSnackbar('An existing user has this name', { variant: 'info' });
    const response = await newUser({ ...value });
    if (response.data.error) enqueueSnackbar(`Error: ${response.data.error}`, { variant: 'error' });
    else if (response.data.user.id) window.location.href = `/dashboard/admin/users/${response.data.user.id}`;
    else enqueueSnackbar(`Error: Could not create user`, { variant: 'error' });
  };

  const handleClickOpen = () => {
    setValue(blankForm());
    setOpen(true);
  };

  const handleClose = () => {
    setValue(blankForm());
    setOpen(false);
  };

  useEffect(() => {
    setPositionsList(positions?.map((position, index) => ({ value: index + 1, label: position })) ?? []);
  }, [open, positions]);


  return (
    <>
      <Button
        variant="contained"
        color="secondary"
        sx={{ mr: 1 }}
        size="small"
        onClick={handleClickOpen}
        startIcon={<Iconify icon="eva:plus-outline" />}
      >
        New user
      </Button>

      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          handleClose();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: 'center' }}>
          New User
          <CloseIconButton onClick={handleClose} />
        </DialogTitle>
        <DialogContent sx={{ height: '100%', width: '100%' }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="subtitle1">First name*</Typography>
              <TextField
                name="firstName"
                placeholder="First name"
                value={value.firstName}
                onChange={(e) => setValue({ ...value, firstName: e.target.value })}
                size="small"
                fullWidth
                autoComplete="no-autocomplete"
                id="new-user-first-name"
              />
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1">Last name*</Typography>
              <TextField
                name="lastName"
                placeholder="Last name"
                size="small"
                fullWidth
                value={value.lastName}
                onChange={(e) => setValue({ ...value, lastName: e.target.value })}
                autoComplete="no-autocomplete"
                id="new-user-last-name"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1">Email*</Typography>
              <TextField
                name="email"
                fullWidth
                placeholder="Email"
                size="small"
                value={value.email}
                onChange={(e) => setValue({ ...value, email: e.target.value })}
                autoComplete="no-autocomplete"
                id="new-user-email"
              />
            </Grid>           
            <Grid item xs={12}>
              <Typography variant="subtitle1">Position*</Typography>
              <Autocomplete
                {...SimpleAutocompleteProps}
                {...AddOptionAutocompleteProps}
                onChange={(event, newValue) => {
                  if (typeof newValue === 'string') setValue({ ...value, position: newValue });
                  else if (newValue && newValue.inputValue) {
                    setPositionsList((list) =>
                      [...list.map((item) => item.label), newValue.inputValue].map((pos, index) => ({
                        value: index + 1,
                        label: pos,
                      }))
                    );
                    setValue({ ...value, position: newValue.inputValue });
                  } else setValue({ ...value, position: newValue?.label });
                }}
                value={positionsList.find((position) => position.label === value.position) ?? null}
                options={positionsList}
                onInputChange={(event) => setValue({ ...value, position: event?.target?.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1">Facility*</Typography>
              <Autocomplete
                {...SimpleAutocompleteProps}
                value={facilities.find((facility) => facility.value === value.facilityId) ?? null}
                onChange={(e, newValue) =>
                  setValue({ ...value, facilityId: newValue?.value, facility: newValue?.label })
                }
                options={facilities}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1">Password</Typography>
              <TextField
                name="password"
                placeholder="Password"
                size="small"
                fullWidth
                value={value.password}
                error={invalidPassword}
                helperText={invalidPassword ? "Password must contain 8 characters, a number, a capital letter, a lowercase letter, and a special character." : ""}
                onChange={(e) => setValue({ ...value, password: e.target.value })}
                autoComplete="no-autocomplete"
                id="new-user-password"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1">Copy From User (optional)</Typography>
              <Autocomplete
                fullWidth
                value={value.copyUserId}
                onChange={(event, newValue) => {
                  setValue({ ...value, copyUserId: newValue });
                }}
                options={userList}
                {...SimpleAutocompleteProps}
              />
            </Grid>
            <Grid item xs={12}>
              <Checkbox
                checked={value.dontSendConfirmEmail}
                onChange={(e) => setValue({ ...value, dontSendConfirmEmail: e.target.checked })}
              />
              Don't send confirmation email
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="error" size="medium" sx={{ width: 150, mr: 2 }} onClick={handleClose}>
            Cancel
          </Button>
          <AwaitButton
            variant="contained"
            color="primary"
            size="medium"
            sx={{ width: 150 }}
            onClick={handleSave}
            disabled={!value.firstName || !value.lastName || !value.email || !value.facility || !value.position}
          >
            Save
          </AwaitButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
